import { fetchCheckoutSession } from '@/requests';
import { Anchor, Card, Container, Group, Loader, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { currency } from '../RichTextEditor/utils';

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: () => fetchCheckoutSession(searchParams.get('session_id')),
  });
  const session = data?.session;

  if (isLoading) {
    return (
      <Container w="100%" h="100vh">
        <Group w="100%" h="100%" align="center" justify="center">
          <Loader color="blue" />
        </Group>
      </Container>
    );
  }

  if (!session) {
    return <Text>Checkout session was not found</Text>;
  }

  const lineItem = session.line_items?.data?.[0];

  return (
    <Container size="sm" mt="lg">
      <Card shadow="sm" radius="sm" withBorder ta="center">
        <Group justify="center">
          <ThemeIcon
            variant="gradient"
            radius="100%"
            size={100}
            gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
            mb="md"
          >
            <IconCheck size={80} />
          </ThemeIcon>
        </Group>
        <Title order={2} mb="md">
          Thank you for subscribing to Slides Generator Pro!
        </Title>
      </Card>

      <Card shadow="sm" radius="sm" withBorder mt="lg">
        <Group justify="space-between">
          <Text fw="bold" fz={18}>
            {lineItem?.description}
          </Text>
          <Text fw="bold" fz={18}>
            ${currency(session.amount_subtotal)} billed {lineItem.price?.recurring?.interval}ly
          </Text>
        </Group>
        <Group justify="space-between" mt="md" style={{ fontWeight: 'bold' }}>
          <Text>Tax</Text>
          <Text>${currency(lineItem.amount_tax)}</Text>
        </Group>
        <Group justify="space-between" mt="md" style={{ fontWeight: 'bold' }}>
          <Text>Total paid today</Text>
          <Text>${currency(lineItem.amount_total)}</Text>
        </Group>
      </Card>

      {/* Usage Instructions Section */}
      <Card shadow="sm" radius="sm" withBorder mt="lg">
        <Text>Your payment has been successfully processed.</Text>
        <Text mt="sm">
          Return to the <strong>Slides Generator</strong> app and refresh the page to start enjoying
          the paid version.
        </Text>
        <Text mt="sm">
          Visit our{' '}
          <Anchor href="/help/get-started" target="_blank">
            Help Center
          </Anchor>{' '}
          for how-to guides and more information on how to make the most of Slides Generator.
        </Text>
      </Card>

      {/* Subscription Management Section */}
      <Card shadow="sm" radius="sm" withBorder mt="lg">
        <Title order={3} mb="md">
          Managing your subscription
        </Title>
        <Text>
          Visit{' '}
          <Anchor href="https://billing.stripe.com/p/login/14k02t8QRcD03yUdQQ" target="_blank">
            Manage Your Subscription
          </Anchor>{' '}
          to cancel your subscription at any time.
        </Text>
        <Text mt="sm">
          See{' '}
          <Anchor href="/help/how-do-i-cancel-my-subscription" target="_blank">
            How Do I Cancel My Subscription?
          </Anchor>{' '}
          for more detailed instructions.
        </Text>
      </Card>
    </Container>
  );
}
